import * as React from "react";
import { Loading, TitleBar, WidgetWrapper } from "uxp/components";
import { IUIPropertyDefinitionMap, IWidgetTemplateProps, registerCardTemplate } from "../../../common/common";
import { generateIconProp, generateIconPropAsSting } from "../../../common/icon-selector/IconEditor";
import SpinnerLoader from "../../../common/LoadingSpinner";
import { SelectableItem, selectUIProps } from "../../../common/SelectableItem";
import { BooleanProperty } from "../../../common/ui-props/BooleanProperty";
import { ContainerProperty, extractContainerProperties } from "../../../common/ui-props/ContainerProperty";
import { SimpleTextProperty } from "../../../common/ui-props/SimpleTextProperty";
import { TextFieldProperty } from "../../../common/ui-props/TextFieldProperty";
import { IContextData, useInitialContextData } from "../../../../hooks/useContextData";
import { replaceWithData } from "../../../../util";


const XPRProps: IUIPropertyDefinitionMap = {}

const DEFAULT_URL = "https://www.youtube.com/watch?v=uoFc6rI3Pao"

const XPR: React.FunctionComponent<IWidgetTemplateProps> = props => {

    let uiProps = props?.uiProps || {};

    const [loading, setLoading] = React.useState<boolean>(true)
    const [contextData, setContextData] = React.useState<IContextData>({})

    const XPRProps: IUIPropertyDefinitionMap = {
        'title': TextFieldProperty(
            'Title',
            { text: '', backgroundColor: 'transparent', textColor: '#000000', fontSize: '1em' },
            { isBackgroundColorHidden: true, isFontSizeHidden: true, useCustomColorPicker: true }
        ),
        'titleHidden': BooleanProperty('Hide Title', false),
        "container": ContainerProperty({
            background: {
                color: "#ffffff",
                image: {
                    image: "",
                    position: "",
                    size: "",
                },
            }
        }),
        'url': SimpleTextProperty('YouTube URL', '', true),
        'autoplay': BooleanProperty('Auto Play', false),
        'mute': BooleanProperty('Mute', false),
    }

    const { isLoading, initialContextData } = useInitialContextData(props)

    React.useEffect(() => {
        setLoading(isLoading)
        setContextData(prev => ({ ...prev, ...initialContextData }))
    }, [isLoading, initialContextData])

    let titleHidden = uiProps?.titleHidden;
    let title = replaceWithData(uiProps?.title?.text?.trim() || 'Youtube', contextData);
    let titleColor = uiProps?.title?.textColor || '#000000';
    let titleBgColor = uiProps?.title?.backgroundColor || 'transparent';

    let { styles: containerStyles } = extractContainerProperties(uiProps.container);

    let autoplay = !!uiProps?.autoplay;
    let mute = !!uiProps?.mute;

    function generateEmbededYoutubeURL(url: string) {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);

        const videoId = (match && match[2].length === 11)
            ? match[2]
            : null;

        return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? '1' : '0'}&mute=${mute ? '1' : '0'}`;
    }

    let url = generateEmbededYoutubeURL(replaceWithData(uiProps?.url?.trim() || '', contextData) || DEFAULT_URL);

    return (
        <WidgetWrapper className='uxp-youtube-card-template'>
            <SelectableItem
                style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
                designer={props.designer}
                props={selectUIProps(uiProps, XPRProps, 'title', 'titleHidden', 'container', 'url', 'autoplay', 'mute')}
                elementId={'body-container'}
                contextData={contextData}
            >
                <div className='container' style={{ ...containerStyles, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {!titleHidden && <div className='title-bar-wrapper'>
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'title', 'titleHidden', 'container', 'url', 'autoplay', 'mute')}
                            contextData={contextData}
                        >
                            <div className="uxp-widget-title-bar uxp-drag-handler" style={{ backgroundColor: titleBgColor }}>
                                <div className="title-section">
                                    <span className="txt" style={{ color: titleColor }}>{title}</span>
                                </div>
                                <div className="action-section"></div>
                            </div>
                        </SelectableItem>
                    </div>
                    }

                    <div className="video-container" style={{ flexGrow: '1' }}>
                        {loading && <SpinnerLoader />}
                        <SelectableItem
                            designer={props.designer}
                            props={selectUIProps(uiProps, XPRProps, 'url', 'autoplay', 'mute')}
                            contextData={contextData}
                        >
                            <iframe
                                style={{ width: '100%', height: '100%' }}
                                src={url}
                                frameBorder="0"
                                allowFullScreen
                                loading="eager"
                                onLoad={() => setLoading(false)}
                            >
                            </iframe>
                        </SelectableItem>
                    </div>
                </div>
            </SelectableItem>
        </WidgetWrapper>
    )
}


registerCardTemplate({
    id: 'youtube-card',
    name: "YouTube Card",
    description: 'A card that can play YouTube video',
    template: XPR,
    icon: generateIconProp('youtube'),
    complexity: 'easy',
    layout: {
        w: 10,
        h: 9
    }
});